import Seo from "@/components/Seo";
import Breadcrumbs, { breadcrumbSchema } from "@/components/site/Breadcrumbs";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { CONTACT } from "@/lib/products";
import { trackWhatsAppClick } from "@/lib/analytics";

export const FAQS = [
  {
    q: "How do I place an order?",
    a: 'Simply click "Order via WhatsApp" on any product, or message us directly at +91 7095255566 with what you\'re looking for. We\'ll guide you through photo submission, wording, and pricing from there.',
  },
  {
    q: "What's the delivery time?",
    a: "Inside Hyderabad: 2–3 business days. Across India: 4–6 business days.",
  },
  {
    q: "What weights are available for the silver coins?",
    a: "Our photo-engraved silver coins come in 5g, 10g, 20g, 30g, and 100g, across several frame styles — from compact acrylic stands to our showpiece 100g grand tombstone frame.",
  },
  {
    q: "Can I use any photograph, or does it need to meet certain requirements?",
    a: "Clear, well-lit, front-facing photographs give the best engraving results. If your photo needs adjustment, we'll let you know before we begin — so there are no surprises once your piece is finished.",
  },
  {
    q: "How many words can be engraved?",
    a: "This varies by product — most coins allow around 4–6 words on the coin itself and up to 8–10 words on the surrounding frame or base. We'll confirm the exact limit for your chosen design when you message us.",
  },
  {
    q: "How much does a piece cost?",
    a: "Pricing depends on the product, weight, and customisation details. Message us on WhatsApp with what you're interested in, and we'll share a clear quote right away.",
  },
  {
    q: "Do you make bracelets too?",
    a: "Yes — our Designer Stretchable Gold Bracelets feature an 18ct gold ID plate with your initials, name, or a sacred symbol, with optional cubic zirconia detailing. Available from 1g to 10g of gold, in Black, Midnight Blue, Brown, Purple, or full 18ct Gold bands.",
  },
  {
    q: "Do you take corporate or bulk orders?",
    a: "Yes — we regularly create personalised recognition pieces for corporate milestones, employee appreciation, and brand partnerships. Visit our Corporate page or message us directly to discuss bulk requirements.",
  },
  {
    q: "What occasions are these gifts suited for?",
    a: "Weddings, anniversaries, birthdays, corporate honors, retirements, memorials, and any moment you want to preserve rather than let fade.",
  },
  { q: "Do you deliver outside India?", a: "Currently, we deliver across India only." },
];

const breadcrumbItems = [
  { label: "Home", href: "/" },
  { label: "FAQ", href: "/faq" },
];

export const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

// Shared FAQ content — rendered standalone on /faq (its own <Seo>/<h1>/breadcrumbs,
// for direct sharing and SEO) and embedded (as a plain <h2> section, no <Seo>) directly
// on the homepage, right before the footer, so visitors don't have to click through.
export default function FAQSection({ standalone = false }) {
  return (
    <section
      id="faq"
      className={standalone ? "relative pt-32 md:pt-40 pb-24" : "relative py-24 md:py-32 border-t border-white/5"}
      data-testid="faq-section"
    >
      <div className="max-w-3xl mx-auto px-5 md:px-10">
        {standalone && (
          <>
            <Seo
              title="Frequently Asked Questions | Shine on Coin"
              description="Answers to common questions about ordering, delivery times, silver coin weights, engraving word limits, pricing and corporate gifting. Order via WhatsApp."
              path="/faq"
              jsonLd={[breadcrumbSchema(breadcrumbItems), faqSchema]}
            />
            <Breadcrumbs items={breadcrumbItems} testId="faq-breadcrumbs" />
          </>
        )}

        <div className={standalone ? "mt-6" : ""}>
          <div className="text-xs uppercase tracking-widest-2 text-[#d4af37]">
            {standalone ? "Need to know" : "Section 04 · FAQ"}
          </div>
          {standalone ? (
            <h1 className="mt-4 font-display text-4xl md:text-6xl leading-[1.05] text-white" data-testid="faq-heading">
              Frequently Asked <span className="italic gold-gradient-text">Questions</span>
            </h1>
          ) : (
            <h2 className="mt-4 font-display text-4xl md:text-6xl leading-[1.05] text-white" data-testid="faq-heading">
              Frequently Asked <span className="italic gold-gradient-text">Questions</span>
            </h2>
          )}
          <p className="mt-4 text-white/70 md:text-lg">
            Everything you need to know before you order. Can't find your answer? Message us on WhatsApp.
          </p>
        </div>

        <div className="mt-10">
          <Accordion type="single" collapsible data-testid="faq-accordion">
            {FAQS.map((f, i) => (
              <AccordionItem key={f.q} value={`faq-${i}`} className="border-white/10">
                <AccordionTrigger
                  className="text-left text-white text-base md:text-lg hover:no-underline"
                  data-testid={`faq-trigger-${i}`}
                >
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-white/60 leading-relaxed" data-testid={`faq-answer-${i}`}>
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <a
            href={CONTACT.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackWhatsAppClick({ placement: "faq_section" })}
            data-testid="faq-whatsapp-cta"
            className="mt-10 inline-flex items-center rounded-full px-6 py-3 bg-[#25D366] text-black font-medium text-sm hover:brightness-110 transition"
          >
            Ask Us on WhatsApp
          </a>
        </div>
      </div>
    </section>
  );
}
